import React from "react"
import { useNavigate } from "react-router-dom"
import { useForm } from "react-hook-form"
import {
	Text,
	Flex,
	Button,
	TextField,
	Checkbox,
	Icon,
} from "@capioweb/ui-components/src"
import {
	CapioSharedLoginFormStyled,
	CapioSharedLoginFormFooterStyled,
} from "../styled"
import {
	LoginSharedFormContext,
	LoginSharedFormVariants,
} from "../context/LoginSharedFormContext"
import { PUBLIC_PAGE_API_ENDPOINT } from "../../../constants"
import { SharedFormProps } from "../../../@types/CapioWeb/core"
import { LocalesContext } from "../../../context"
import { getAntiforgeryToken } from "../../../utils/antiforgery"
import { StyledButtonContent, StyledIcon } from '../../../components/MyPagesUserLifestyleFormBlock/LifestyleForm'

interface LoginSharedForm {
	forms: {
		loginForm?: SharedFormProps
		forgotPasswordForm?: SharedFormProps
		resetPasswordForm?: SharedFormProps
		resetSuccessForm?: SharedFormProps
	}
}

const LoginSharedForm: React.FC<LoginSharedForm> = (props) => {
	const {
		handleSubmit,
		register,
		reset,
		formState: { errors },
	} = useForm({
		mode: "onChange",
	})
	const navigate = useNavigate()
	const { translate, translationKeys } = React.useContext(LocalesContext)
	const [loading, setLoading] = React.useState<boolean>(false)


	const { activeFormVariant, setActiveFormVariant } = React.useContext(
		LoginSharedFormContext
	)

	const [activeForm, setActiveForm] = React.useState(
		props.forms[activeFormVariant]
	)

	const [submitField, setSubmitField] = React.useState(
		activeForm.fields.find((field) => field.type === "submit")
	)
	const [feedbackMsg, setFeedbackMsg] =
		React.useState<{
			success: boolean
			message: string
		}>(null)

	const [errorMessages, setErrorMessages] = React.useState([])

	React.useEffect(() => {
		// Reset hook forms registrations
		reset()

		// Set new active form based on form type
		setActiveForm(props.forms[activeFormVariant])
		setSubmitField(
			props.forms[activeFormVariant].fields.find(
				(field) => field.type === "submit"
			)
		)
	}, [activeFormVariant, setSubmitField, setActiveForm, reset])

	const onSubmit = async (data: Record<string, string>) => {
		setLoading(true)
		const url = PUBLIC_PAGE_API_ENDPOINT + activeForm.action
		const body = Object.keys(data)
			.map((key) => key + "=" + data[key])
			.join("&")

		var antiforgeryToken = await getAntiforgeryToken()

		await fetch(url, {
			method: activeForm.method,
			body,
			headers: {
				RequestVerificationToken: antiforgeryToken,
				Accept: "application/json",
				"Content-Type": "application/x-www-form-urlencoded",
			},
			credentials: "include",
		})
			.then((res) => {
				return res.json().then((json) => ({
					success: res.status === 200,
					data: json,
					statusCode: res.status,
				}))
			})
			.then((result: { success: boolean; data: any; statusCode: number }) => {
				if (activeFormVariant === "resetPasswordForm") {
					if (result.success) {
						setActiveFormVariant("resetSuccessForm")
					} else {
						if (result.data?.redirect && result.data.redirect !== "") {
							navigate(result.data.redirect)
						} else {
							setFeedbackMsg({
								message: result.data.message,
								success: false,
							})
							if (result.data.errors && result.data.errors.length > 0) {
								const errors = result.data.errors.map((item) => {
									return item.message
								})
								setErrorMessages(errors)
							}
						}
					}
				} else {
					if (result.data.redirect && result.data.redirect !== "") {
						navigate(result.data.redirect)
					} else {
						if (result.data.message) {
							setFeedbackMsg({
								message: result.data.message,
								success: result.success,
							})
						}
						if (result.data.errors?.length > 0) {
							const errors = result.data.errors.map((item) => {
								return item.message
							})
							setErrorMessages(errors)
						}
					}
				}
			})
			.catch((e) => {
				console.error(e)
			})
		setLoading(false)
	}

	return (
		<CapioSharedLoginFormStyled
			action={activeForm.action}
			encType="application/x-www-form-urlencoded"
			method={activeForm.method}
			onSubmit={handleSubmit(onSubmit)}
		>
			<Flex
				w={1}
				flexDirection="column"
				pt={{ _: 3, m: 5 }}
				pb={3}
				px={{ _: 4, m: 9 }}
			>
				{activeForm.heading && (
					<Text variant="headline-1" mb={3}>
						{activeForm.heading}
					</Text>
				)}
				{activeForm.description && <Text>{activeForm.description}</Text>}

				{/* Submit button for resetSuccessForm */}
				{activeFormVariant === "resetSuccessForm" && submitField && (
					<Flex justifyContent="center" mt={4} mb={3}>
						<Button type="submit">{submitField.label}</Button>
					</Flex>
				)}

				{activeForm.fields.map((field) => {
					const isInputField = !["rte", "captcha", "submit"].includes(
						field.type
					)

					if (field && field.type && isInputField) {
						const { type, value, required, ...rest } = field
						// https://react-hook-form.com/api/useform/register
						const { ref, ...registerProps } = register(field.name, {
							required: {
								value: field.type !== "checkbox" ? true : false,
								message: activeForm.requiredMessage,
							},
						})
						const fieldProps = {
							...registerProps,
							...rest,
							type,
							key: field.id,
							error: errors[field.name],
							...(field.type !== "hidden" &&
								errors[field.name] && {
								helperText: errors[field.name].message,
							}),
						}

						if (["text", "email", "password"].includes(type)) {
							return <TextField inputRef={ref} {...fieldProps} />
						} else {
							return {
								checkbox: <Checkbox inputRef={ref} {...fieldProps} />,
								hidden: (
									<input
										ref={ref}
										readOnly
										key={field.value}
										value={field.value}
										{...fieldProps}
									/>
								),
							}[type]
						}
					}
				})}
			</Flex>
			{activeFormVariant !== "resetSuccessForm" && (
				<CapioSharedLoginFormFooterStyled
					w={1}
					flexDirection="column"
					py={3}
					px={{ _: 4, m: 9 }}
				>
					<Flex
						flexDirection="row"
						alignItems="center"
						justifyContent="space-between"
					>
						{activeFormVariant !== "resetPasswordForm" ? (
							<Text.Anchor
								href="#"
								onClick={(e) => {
									e.preventDefault()
									setFeedbackMsg(null)
									setActiveFormVariant(
										{
											loginForm:
												"forgotPasswordForm" as LoginSharedFormVariants,
											forgotPasswordForm:
												"loginForm" as LoginSharedFormVariants,
										}[activeFormVariant]
									)
								}}
								style={{ textDecoration: "underline" }}
							>
								{
									{
										loginForm: translate(
											translationKeys.forgotPasswordQuestion
										),
										forgotPasswordForm: translate(translationKeys.backToLogin),
									}[activeFormVariant]
								}
							</Text.Anchor>
						) : (
							<Text.Link to={activeForm?.abortLink ?? "#"} underline active>
								{translate(translationKeys.abort)}
							</Text.Link>
						)}

						{/* Submit button */}
						{submitField && (
							<Button ml="auto" style={{ position: 'relative' }} type={loading ? 'button' : "submit"}>
								<StyledIcon aria-hidden loading={loading}>
									<Icon icon="ellipse" color='white' />
								</StyledIcon>
								<StyledButtonContent loading={loading}>
									{submitField.label}
								</StyledButtonContent>
							</Button>
						)}
					</Flex>

					{/* Feedback message - used if we don't redirect (errors & success) */}
					<Flex
						h={feedbackMsg ? "auto" : "0"}
						overflow="hidden"
						w={1}
						mt={1.5}
						justifyContent="flex-end"
						role="region"
						aria-live="polite"
					>
						<Text
							as="span"
							textColor={feedbackMsg?.success ? "primary" : "alert"}
						>
							{feedbackMsg?.message}
						</Text>
					</Flex>
					{errorMessages?.length > 0 &&
						errorMessages.map((error, i) => {
							const fieldNames = activeForm.fields.map((field) => field.name)
							if (fieldNames.includes(error.key)) {
								return null
							}
							return (
								<Flex w={1} mt={1.5} key={i + error.key}>
									<Text
										as="span"
										textColor="alert"
										variant="paragraph-14"
										mb={0}
									>
										{error}
									</Text>
								</Flex>
							)
						})}
				</CapioSharedLoginFormFooterStyled>
			)}
		</CapioSharedLoginFormStyled>
	)
}

export { LoginSharedForm }
